import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Star, MapPin, Hammer, Clock, ThumbsUp, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import LocalProof from "@/components/site/LocalProof";
import PatternStudio from "@/components/site/PatternStudio";
import { COMPANY, SERVICES, SERVICE_AREAS, SORTED_AREAS } from "@/lib/constants";
import api from "@/lib/api";

const HERO = "/archive/firepit-hero.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const trust = [
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: ThumbsUp, label: "Free Estimates" },
  { icon: MapPin, label: `Local Since ${COMPANY.founded}` },
  { icon: Star, label: "5-Star Rated" },
];

const why = [
  { icon: Hammer, title: "Craftsmanship First", text: "Every pour is prepped, reinforced, and finished by an experienced crew that treats your property like their own." },
  { icon: Clock, title: "On-Time, Clean Job Sites", text: "We show up when we say we will, protect your landscaping, and leave the site spotless." },
  { icon: ShieldCheck, title: "Built for WI Winters", text: "Proper base, drainage, and reinforcement so your concrete survives freeze-thaw for decades." },
];

export default function Home() {
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    name: COMPANY.name,
    telephone: COMPANY.phone,
    email: COMPANY.email,
    address: { "@type": "PostalAddress", streetAddress: COMPANY.address, addressLocality: COMPANY.city, addressRegion: COMPANY.state, postalCode: COMPANY.zip },
    areaServed: SERVICE_AREAS.map((a) => `${a.city}, ${a.state}`),
    priceRange: "$$",
  };

  const ba = gallery.find((g) => g.type === "before_after");

  return (
    <div>
      <SEO
        title="CG Concrete LLC | Concrete Contractor in Watertown, WI"
        description="Premium concrete driveways, patios, stamped concrete & floors in Watertown, WI. Locally owned, licensed & insured. Free estimates — call (920) 253-5606."
        schema={schema}
      />

      {/* Hero */}
      <section className="relative bg-secondary text-white overflow-hidden">
        <img src={HERO} alt="Stamped concrete fire-pit patio at sunset by CG Concrete in Watertown, WI" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-secondary/70" />
        <div className="absolute inset-0 grain-texture" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <motion.div initial="hidden" animate="show" className="max-w-3xl">
            <motion.div variants={fadeUp} custom={0} className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 text-primary-foreground px-4 py-1.5 text-xs font-bold tracking-wider mb-6">
              <MapPin className="h-3.5 w-3.5" /> SERVING WATERTOWN, WI & SURROUNDING AREAS
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[0.95]">
              Pull Up a Chair. <span className="text-primary">We'll Pour the Rest.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-6 text-lg text-white/80 leading-relaxed max-w-xl">
              From fire-pit patios to driveways that shrug off Wisconsin winters, CG Concrete builds the outdoor spaces Watertown gathers around — prepped right, poured clean, and finished to last.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild className="h-13 px-8 py-6 bg-primary hover:bg-primary/90 rounded-sm font-bold text-base hover:-translate-y-1 transition-transform" data-testid="hero-quote-btn">
                <Link to="/contact">Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button asChild variant="outline" className="h-13 px-8 py-6 rounded-sm font-bold text-base bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary" data-testid="hero-call-btn">
                <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-5 w-5" /> {COMPANY.phone}</a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          {trust.map((t) => (
            <div key={t.label} className="flex items-center gap-3 py-5 px-4 justify-center" data-testid={`trust-${t.label}`}>
              <t.icon className="h-5 w-5 text-primary shrink-0" />
              <span className="font-bold text-sm sm:text-base">{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Services bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-12">
          <div>
            <span className="text-primary font-bold text-sm tracking-widest">WHAT WE DO</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-2">Full-Service Concrete</h2>
          </div>
          <Link to="/services" className="font-bold text-primary flex items-center gap-1 hover:gap-2 transition-all" data-testid="services-viewall">
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {SERVICES.slice(0, 6).map((s, i) => (
            <motion.div key={s.slug} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} custom={i}>
              <Link to={`/services/${s.slug}`} className="group block relative overflow-hidden aspect-[4/3] bg-secondary" data-testid={`home-service-${s.slug}`}>
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 group-hover:opacity-60 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/30 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="font-heading text-xl font-bold text-white">{s.title}</h3>
                  <p className="text-white/70 text-sm mt-1 line-clamp-2">{s.short}</p>
                  <span className="inline-flex items-center gap-1 text-primary font-bold text-sm mt-3 group-hover:gap-2 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Before/After */}
      {ba && (
        <section className="bg-card border-y border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-primary font-bold text-sm tracking-widest">REAL RESULTS</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-2 leading-tight">See the Difference a Fresh Pour Makes</h2>
              <p className="text-muted-foreground mt-4 leading-relaxed text-lg">
                Drag the slider to compare. From cracked, heaving slabs to clean, level concrete — we transform tired surfaces into work you'll be proud to pull into every day.
              </p>
              <Button asChild className="mt-6 bg-secondary hover:bg-secondary/90 rounded-sm font-bold h-12 px-6" data-testid="ba-gallery-btn">
                <Link to="/gallery">Browse Our Project Gallery</Link>
              </Button>
            </div>
            <BeforeAfterSlider before={ba.before_image} after={ba.after_image} className="border border-border" />
          </div>
        </section>
      )}

      {/* Why choose us */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-2xl mb-12">
          <span className="text-primary font-bold text-sm tracking-widest">WHY CG CONCRETE</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-2">The Right Way, Every Time</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {why.map((w, i) => (
            <motion.div key={w.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} className="bg-card border border-border p-8 hover:border-primary transition-colors" data-testid={`why-${i}`}>
              <div className="h-12 w-12 bg-primary/10 flex items-center justify-center mb-5">
                <w.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-bold">{w.title}</h3>
              <p className="text-muted-foreground mt-2 leading-relaxed">{w.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Stamped Concrete Studio teaser */}
      <section className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
            <div className="max-w-xl">
              <span className="text-primary font-bold text-sm tracking-widest">STAMPED CONCRETE</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-2 leading-tight">Design It in Our Color &amp; Pattern Studio</h2>
              <p className="text-muted-foreground mt-4 leading-relaxed text-lg">
                Pick a color and preview it instantly across our signature stamp patterns. Find your perfect look before we ever pour.
              </p>
            </div>
            <Link to="/pattern-studio" className="font-bold text-primary flex items-center gap-1 hover:gap-2 transition-all shrink-0" data-testid="home-studio-link">
              Open the full studio <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <PatternStudio compact />
        </div>
      </section>


      {/* Gallery preview */}
      {gallery.length > 0 && (
        <section className="bg-secondary text-white grain-texture">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-12">
              <div>
                <span className="text-primary font-bold text-sm tracking-widest">RECENT WORK</span>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-2">Project Gallery</h2>
              </div>
              <Link to="/gallery" className="font-bold text-primary flex items-center gap-1 hover:gap-2 transition-all">View full gallery <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="group relative aspect-square overflow-hidden" data-testid={`home-gallery-${g.id}`}>
                  <img src={g.type === "before_after" ? g.after_image : g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <div>
                      <p className="font-bold">{g.title}</p>
                      <p className="text-xs text-white/70">{g.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-primary font-bold text-sm tracking-widest">WHAT NEIGHBORS SAY</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-2">Reviews From Real Customers</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.slice(0, 3).map((t) => (
              <div key={t.id} className="bg-card border border-border p-8 flex flex-col" data-testid={`home-testimonial-${t.id}`}>
                <Quote className="h-8 w-8 text-primary/30" />
                <div className="flex gap-0.5 my-3">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
                <p className="text-foreground/90 leading-relaxed flex-1">"{t.text}"</p>
                <div className="mt-5 pt-4 border-t border-border">
                  <p className="font-bold">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.service} · {t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <LocalProof />

      {/* Service areas */}
      <section className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Proudly Serving Southeast Wisconsin</h2>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {SORTED_AREAS.map((a) => (
              <Link key={a.slug} to={`/service-areas/${a.slug}`} className="px-5 py-2.5 border border-border hover:border-primary hover:text-primary font-semibold text-sm transition-colors" data-testid={`area-chip-${a.slug}`}>
                {a.city}, {a.state}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="relative bg-secondary text-white overflow-hidden grain-texture p-10 sm:p-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold">Ready to Break Ground?</h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto text-lg">Get a free, no-pressure estimate. Tell us about your project and we'll take it from there.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="h-13 px-8 py-6 bg-primary hover:bg-primary/90 rounded-sm font-bold text-base" data-testid="cta-quote-btn">
              <Link to="/contact">Request Free Quote <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-13 px-8 py-6 rounded-sm font-bold text-base bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary">
              <a href={`tel:${COMPANY.phoneRaw}`}><Phone className="mr-2 h-5 w-5" /> {COMPANY.phone}</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

